import React, { useState } from 'react';
import { Calendar, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { parseDate } from '../../utils/dates';
import StatusBadge from './StatusBadge';
import StatusTimeline from './StatusTimeline';
import './RequestCard.css';

function RequestCard({ request, timeline, onCancel, showUser = false, compact = false }) {
  const [expanded, setExpanded] = useState(false);
  const days = request.days || [];
  const firstDate = days.length > 0 ? days[0].date : null;
  const lastDate = days.length > 0 ? days[days.length - 1].date : null;

  // Use parseDate to safely handle both Date objects (from pg driver) and strings
  const formatDateDisplay = (dateVal) => {
    const d = parseDate(dateVal);
    return d ? format(d, 'MMM d, yyyy') : '';
  };

  const dateRange = firstDate && lastDate
    ? firstDate === lastDate
      ? formatDateDisplay(firstDate)
      : `${formatDateDisplay(firstDate)} - ${formatDateDisplay(lastDate)}`
    : 'No dates';

  const canCancel = ['pending', 'approved'].includes(request.status);

  return (
    <div className={`request-card ${compact ? 'request-card-compact' : ''}`}>
      <div className="request-card-header" onClick={() => setExpanded(!expanded)}>
        <div className="request-card-info">
          <div className="request-card-top-line">
            {showUser && (
              <span className="request-card-user">
                {request.first_name || request.user_name}
              </span>
            )}
            <StatusBadge status={request.status} />
            {request.category === 'birthday' && (
              <span className="request-card-category">Birthday</span>
            )}
          </div>
          <div className="request-card-dates">
            <Calendar size={14} />
            <span>{dateRange}</span>
            <span className="request-card-total">
              {request.total_days} day{request.total_days !== 1 ? 's' : ''}
            </span>
          </div>
          {request.is_short_notice && (
            <div className="request-card-flag request-card-flag-warning">
              <AlertTriangle size={12} />
              <span>Short notice</span>
            </div>
          )}
          {request.insufficient_balance && (
            <div className="request-card-flag request-card-flag-warning">
              <AlertTriangle size={12} />
              <span>Insufficient balance</span>
            </div>
          )}
        </div>
        <div className="request-card-toggle">
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>

      {expanded && (
        <div className="request-card-details">
          {/* Day details */}
          <div className="request-card-days">
            {days.map((day, i) => {
              const d = parseDate(day.date);
              return (
              <div key={i} className="request-card-day">
                <span className="request-card-day-date">
                  {d ? format(d, 'EEE, MMM d') : ''}
                </span>
                <span className="request-card-day-type">
                  {day.day_type === 'full' ? 'Full day' :
                   day.day_type === 'half_am' ? 'Half day (AM)' : 'Half day (PM)'}
                </span>
              </div>
              );
            })}
          </div>

          {/* Notes */}
          {request.notes && (
            <div className="request-card-notes">
              <strong>Notes:</strong> {request.notes}
            </div>
          )}

          {/* Denial reason */}
          {request.denial_reason && (
            <div className="request-card-denial">
              <strong>Denial reason:</strong> {request.denial_reason}
            </div>
          )}

          {/* Removal reason */}
          {request.removal_reason && (
            <div className="request-card-denial">
              <strong>Removal reason:</strong> {request.removal_reason}
            </div>
          )}

          {/* Insufficient balance note */}
          {request.insufficient_balance_note && (
            <div className="request-card-notes">
              <strong>Balance note:</strong> {request.insufficient_balance_note}
            </div>
          )}

          {/* Draw info */}
          {request.status === 'approved' && request.days_from_regular !== null && (
            <div className="request-card-draw">
              Regular: {request.days_from_regular}
              {request.days_from_bonus > 0 && ` | Bonus: ${request.days_from_bonus}`}
            </div>
          )}

          {/* Timeline */}
          {timeline && timeline.length > 0 && (
            <div className="request-card-timeline">
              <StatusTimeline timeline={timeline} />
            </div>
          )}

          {/* Actions */}
          {canCancel && onCancel && (
            <div className="request-card-actions">
              <button className="btn-cancel-request" onClick={() => onCancel(request.id)}>
                Cancel Request
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default RequestCard;
